import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { FailedGlyph, PendingGlyph } from "../glyphs.js";
export function Well({ head, headEnd, frame, overlay = false, controls, below, style, ...rest }) {
    return (_jsxs("div", { "data-well": "", ...rest, style: {
            background: "var(--well)",
            color: "var(--well-ink)",
            borderRadius: "var(--radius-panel)",
            padding: "var(--space-15) var(--space-2) 14px",
            minWidth: 0,
            ...style,
        }, children: [_jsxs("div", { "data-well-head": "", style: {
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    gap: "var(--space-2)", minHeight: 26, marginBottom: 10,
                    fontSize: "var(--type-small-size)", lineHeight: "var(--type-small-line)",
                    color: "var(--well-mute)",
                }, children: [_jsx("div", { style: { minWidth: 0, display: "flex", alignItems: "center" }, children: head }), headEnd !== undefined && _jsx("div", { style: { flex: "none" }, children: headEnd })] }), _jsxs("div", { "data-well-frame": "", style: { position: "relative", aspectRatio: "16 / 10", borderRadius: "var(--radius-focus)", overflow: "hidden" }, children: [frame, overlay && (_jsx("div", { "data-well-overlay": "", "aria-hidden": "true", style: { position: "absolute", inset: 0, display: "grid", placeItems: "center", pointerEvents: "none" }, children: _jsx("span", { style: {
                                width: 64, height: 64, borderRadius: "var(--radius-round)", display: "grid", placeItems: "center",
                                paddingLeft: "var(--space-05)", boxSizing: "border-box",
                                background: "color-mix(in srgb, var(--well) 72%, transparent)", color: "var(--well-head)",
                            }, children: _jsx(PlayMark, { size: 22 }) }) }))] }), controls !== undefined && (_jsx("div", { "data-well-controls": "", style: { display: "flex", alignItems: "flex-start", gap: 14, marginTop: "var(--space-15)", minWidth: 0 }, children: controls })), below] }));
}
function PlayMark({ size }) {
    return (_jsx("svg", { width: size, height: size, viewBox: "0 0 24 24", "aria-hidden": "true", children: _jsx("path", { d: "M7 4.5v15L20 12z", fill: "currentColor" }) }));
}
function PauseMark({ size }) {
    return (_jsx("svg", { width: size, height: size, viewBox: "0 0 24 24", "aria-hidden": "true", children: _jsx("path", { d: "M6 4h4.5v16H6zM13.5 4H18v16h-4.5z", fill: "currentColor" }) }));
}
function StepMark({ next }) {
    return (_jsx("svg", { width: 14, height: 14, viewBox: "0 0 24 24", "aria-hidden": "true", children: _jsx("path", { d: next ? "M9 5l7 7-7 7" : "M15 5l-7 7 7 7", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round", strokeLinejoin: "round" }) }));
}
const LABEL = {
    play: "Play",
    pause: "Pause",
    previous: "Previous still",
    next: "Next still",
};
export function WellControl({ kind, disabled = false, style, ...rest }) {
    const step = kind === "previous" || kind === "next";
    const size = step ? 32 : 36;
    const look = kind === "play"
        ? disabled
            ? { background: "var(--well-rail)", color: "var(--well-mute)" }
            : { background: "var(--well-ink)", color: "var(--well)" }
        : kind === "pause"
            ? { background: "transparent", boxShadow: "inset 0 0 0 2px var(--well-ink)", color: "var(--well-ink)" }
            : { background: "transparent", boxShadow: "inset 0 0 0 1.5px var(--well-mute)", color: "var(--well-ink)" };
    return (_jsx("button", { type: "button", "data-well-control": kind, "aria-label": LABEL[kind], title: LABEL[kind], disabled: disabled, ...rest, style: {
            width: size, height: size, flex: "none", border: "none", padding: 0, margin: step ? "2px 0 0" : 0,
            borderRadius: "var(--radius-round)", display: "grid", placeItems: "center",
            cursor: disabled ? "default" : "pointer",
            ...look,
            ...style,
        }, children: kind === "play" ? _jsx(PlayMark, { size: 12 }) : kind === "pause" ? _jsx(PauseMark, { size: 12 }) : _jsx(StepMark, { next: kind === "next" }) }));
}
export function WellMessage({ kind, title, body, action, style, ...rest }) {
    if (kind === "loading") {
        return (_jsxs("div", { role: "status", ...rest, style: {
                position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "center",
                justifyContent: "center", gap: 10, color: "var(--well-mute)", pointerEvents: "none",
                fontSize: "var(--type-body-size)", lineHeight: "var(--type-body-line)", ...style,
            }, children: [_jsx(PendingGlyph, { size: 28 }), _jsx("span", { children: title })] }));
    }
    if (kind === "partial") {
        return (_jsxs("div", { role: "status", ...rest, style: {
                position: "absolute", left: 0, right: 0, top: 0, display: "flex", alignItems: "center", gap: 10,
                padding: "var(--space-1) var(--space-15)", color: "var(--well-ink)",
                background: "color-mix(in srgb, var(--well) 88%, transparent)", pointerEvents: "none",
                fontSize: "var(--type-small-size)", lineHeight: "var(--type-small-line)", ...style,
            }, children: [_jsx(FailedGlyph, { onWell: true }), _jsx("span", { style: { fontWeight: "var(--type-small-weight-strong)" }, children: title }), body !== undefined && _jsx("span", { style: { color: "var(--well-mute)" }, children: body })] }));
    }
    return (_jsxs("div", { role: "alert", ...rest, style: {
            position: "absolute", inset: 0, display: "flex", flexDirection: "column", alignItems: "flex-start",
            justifyContent: "flex-end", gap: 10, padding: "var(--space-35) 30px", color: "var(--well-ink)",
            background: "color-mix(in srgb, var(--well) 88%, transparent)", ...style,
        }, children: [_jsx(FailedGlyph, { size: 26, onWell: true }), _jsx("h2", { style: {
                    margin: 0, fontSize: "var(--type-row-title-size)", lineHeight: "var(--type-row-title-line)",
                    fontWeight: "var(--type-row-title-weight)", letterSpacing: "var(--type-row-title-tracking)",
                }, children: title }), body !== undefined && _jsx("p", { style: { margin: 0, fontSize: "var(--type-body-size)", lineHeight: "var(--type-body-line)", maxWidth: "48ch" }, children: body }), action] }));
}
